import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";
import { useAuth } from "../../auth/AuthContext";
import { LanguageSelector } from "./LanguageSelector";
import { Menu, X } from "lucide-react";

const BrandMark = () => (
  <img src="/logo.png" alt="TrustWall" className="h-9 w-9 rounded-xl object-cover shadow-[0_0_20px_-6px_rgba(13,148,136,0.6)]" />
);

export const Navbar = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/check", label: t("nav.check") },
    { to: "/pricing", label: t("nav.pricing") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/security", label: t("nav.security") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"}`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "tw-glass border-b border-slate-200" : "border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2.5">
          <BrandMark />
          <span className="text-lg font-bold tracking-tight text-slate-900">TrustWall</span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.to.slice(1)}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <LanguageSelector />
          {user && (user.role === "ADMIN" || user.role === "EXPERT") && (
            <Link
              to="/expert"
              data-testid="navbar-expert"
              className="hidden text-sm font-medium text-blue-700 transition-colors hover:text-blue-700 sm:inline"
            >
              {t("expertDash.nav")}
            </Link>
          )}
          {user && user.role === "ADMIN" && (
            <Link
              to="/admin"
              data-testid="navbar-admin"
              className="hidden text-sm font-medium text-blue-700 transition-colors hover:text-blue-700 sm:inline"
            >
              {t("admin.nav")}
            </Link>
          )}
          <Link
            to={user ? "/account" : "/login"}
            data-testid={user ? "navbar-account" : "navbar-login"}
            className="hidden text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 sm:inline"
          >
            {user ? t("account.account") : t("account.login")}
          </Link>
          <Link
            to="/check"
            data-testid="navbar-check-cta"
            className="hidden rounded-full bg-[#1E3A8A] px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] hover:shadow-[0_0_24px_-4px_rgba(56,189,248,0.8)] sm:inline-flex"
          >
            {t("cta.short")}
          </Link>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700 lg:hidden"
            aria-label={t("nav.menu")}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="tw-glass border-t border-slate-200 lg:hidden" data-testid="navbar-mobile-menu">
          <div className="flex flex-col gap-1 px-4 py-4 sm:px-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="rounded-lg px-3 py-2.5 text-base font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/check"
              className="mt-2 rounded-full bg-[#1E3A8A] px-4 py-2.5 text-center text-sm font-semibold text-white"
            >
              {t("cta.short")}
            </Link>
            <Link
              to={user ? "/account" : "/login"}
              className="rounded-lg px-3 py-2.5 text-base font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              {user ? t("account.account") : t("account.login")}
            </Link>
            {user && user.role === "ADMIN" && (
              <Link to="/admin" className="rounded-lg px-3 py-2.5 text-base font-medium text-blue-700 hover:bg-slate-50">
                {t("admin.nav")}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
